/**
 * Calendar dates the way the newsroom writes them: day/month/year (26/10/2026).
 * Storage and the API keep ISO `YYYY-MM-DD`; these helpers convert at the edge of the screen,
 * so the browser's own locale (which renders a native date box as 10/26/2026 on an English
 * Windows or iPhone) never decides what a journalist sees.
 */
const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
/** `2026-10-26` → `26/10/2026`; anything that is not an ISO date comes back unchanged. */
export function formatDayMonthYear(iso) {
    const m = ISO.exec(iso);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}
/**
 * What the box shows while the user types: digits only, slashes placed after the day and the month
 * (`2610` → `26/10`, `26102026` → `26/10/2026`). A pasted ISO date is accepted as well.
 */
export function maskDayMonthYear(typed) {
    const iso = ISO.exec(typed.trim());
    if (iso)
        return `${iso[3]}/${iso[2]}/${iso[1]}`;
    const digits = typed.replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 2)
        return digits;
    if (digits.length <= 4)
        return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}
/**
 * `26/10/2026` (also `26.10.2026`, `26-10-2026`, `26/10/26`) → `2026-10-26`; null when it is not a real
 * calendar date. Two-digit years are read as 2000–2099. Years outside 1900–2100 are refused.
 */
export function parseDayMonthYear(text) {
    const m = /^\s*(\d{1,2})[/.\-](\d{1,2})[/.\-](\d{2}|\d{4})\s*$/.exec(text);
    if (!m)
        return null;
    const day = Number(m[1]);
    const month = Number(m[2]);
    const year = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
    if (year < 1900 || year > 2100 || month < 1 || month > 12 || day < 1)
        return null;
    const probe = new Date(Date.UTC(year, month - 1, day));
    if (probe.getUTCFullYear() !== year || probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day)
        return null;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
//# sourceMappingURL=dates.js.map